import { AnalyticsSkuViewed } from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@ria-client/segment/Analytics/constants';
import { MonetizableEnum } from '@ria-client/segment/enums';
import { MarketplaceEntityTypeEnum } from '@ria-client/supergraph/marketplace/enums';
import type { ProductCard } from '@ria-client/supergraph/roundup/types';
import { stripFederallyInsuredAnnotation } from '@ria-client/utils/cta-utils';
import { ProductCard as LegacyProductCard } from './product-card';
import { ProductCardRedesign } from './redesign/product-card-redesign';

const SECTION_NAME = 'product_cards_section';

interface ProductCardsWrapperProps {
  cardBadgeText?: string;
  card: ProductCard;
  cardIndex: number;
  productPositionBase: number;
  isSeoBankingFFActive: boolean;
  isBankingProductCardsRedesignExperimentActive?: boolean;
}

export function ProductCardsWrapper({
  cardBadgeText,
  card,
  cardIndex,
  productPositionBase,
  isSeoBankingFFActive,
  isBankingProductCardsRedesignExperimentActive,
}: ProductCardsWrapperProps) {
  const entity = card.marketplaceEntity;
  const elementPosition = cardIndex + productPositionBase;
  const baseCta = entity.ctaOverride || entity.cta;
  const cta = isBankingProductCardsRedesignExperimentActive
    ? stripFederallyInsuredAnnotation(baseCta, entity.federallyInsuredAnnotation)
    : baseCta;
  const { drivers, entityType, institution, name, offerForCta, product, productType } = entity;
  const monetizable = offerForCta?.monetizable ?? entity.monetizable ?? MonetizableEnum.no;
  const offerId = offerForCta?.id || entity.id;
  const offerName = offerForCta?.name || name;
  const isInstitution = entityType === MarketplaceEntityTypeEnum.Institutions;
  const ctaAnalytics = {
    index: elementPosition,
    ...(institution?.id && { institutionId: institution.id }),
    ...(isInstitution && { institutionId: entity.id }),
    offerId,
    ...(product?.id && { productId: product.id }),
    productType,
    monetizable,
  };
  const ProductCardComponent = isBankingProductCardsRedesignExperimentActive
    ? ProductCardRedesign
    : LegacyProductCard;

  return (
    <li className={cardIndex === 0 ? 'list-none' : 'list-none mt-5 md:mt-8'}>
      <AnalyticsSkuViewed
        as="div"
        additionalAttributes={{ page_type: 'roundup' }}
        impressionPosition={elementPosition}
        impressionSection={SECTION_NAME}
        institutionId={isInstitution ? entity.id : institution?.id}
        institutionName={isInstitution ? name : institution?.name}
        level="offer"
        monetizable={monetizable}
        name={offerName}
        offerId={offerId}
        offerName={offerName}
        productId={product?.id}
        productName={product?.name}
        productType={productType}
        source={SEGMENT_SKU_VIEWED_SOURCE}
      >
        <ProductCardComponent
          bottomLine={card.bottomLine}
          cardBadgeText={cardBadgeText}
          cons={card.cons || entity.structuredContentCons}
          cta={cta || undefined}
          ctaAnalytics={ctaAnalytics}
          displayName={entity.displayName}
          drivers={drivers}
          federallyInsuredAnnotation={entity.federallyInsuredAnnotation}
          fiveYearApyEntity={card.fiveYearApyEntity}
          image={entity.image}
          isFidelityOffer={entity.isFidelityOffer}
          isRiaOffer={entity.isRiaOffer}
          isSeoBankingFFActive={isSeoBankingFFActive}
          monetizable={monetizable}
          oneYearApyEntity={card.oneYearApyEntity}
          periodConsideredDate={entity.periodConsideredDate}
          productType={entity.productType}
          pros={card.pros || entity.structuredContentPros}
          reviewedAtDate={entity.reviewedAtDate}
          reviewLink={entity.reviewLink}
          riaDisclaimerText={entity.riaDisclaimerText}
          starRating={entity.starRating}
          starRatingTooltip={entity.starRatingTooltip}
          structuredContentTagline={entity.structuredContentTagline}
          subProductType={entity.subProductType}
          threeYearApyEntity={card.threeYearApyEntity}
        />
      </AnalyticsSkuViewed>
    </li>
  );
}
